import React, { useState, useEffect } from "react";
import {
    Card,
    Form,
    Input,
    Button,
    Typography,
    Divider,
    message,
    Alert,
} from "antd";
import {
    UserOutlined,
    MailOutlined,
    LockOutlined,
    SaveOutlined,
} from "@ant-design/icons";
import { adminAPI } from "../../utils/api";
import { useAuth } from "../../contexts/AuthContext";

const { Title, Text } = Typography;

const Profile = () => {
    const { user, updateUser } = useAuth();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) {
            form.setFieldsValue({
                name: user.name,
                email: user.email,
            });
        }
    }, [user, form]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const response = await adminAPI.updateProfile(values);
            message.success(response.data.message || "Profile updated successfully");

            // Update session if user details changed
            if (response.data.user) {
                updateUser(response.data.user);
            }

            form.resetFields(["currentPassword", "newPassword", "confirmPassword"]);
        } catch (error) {
            console.error("Update profile error:", error);
            const errorMsg = error.response?.data?.error || "Failed to update profile";
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
            <Title level={2}>Admin Profile</Title>
            <Text type="secondary">
                Manage your account settings, including email and password.
            </Text>
            <Divider />

            <Card bordered={false} className="shadow-sm">
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        name: user?.name,
                        email: user?.email,
                    }}
                >
                    <Title level={4} style={{ marginBottom: 24 }}>
                        Personal Information
                    </Title>

                    <Form.Item
                        name="name"
                        label="Full Name"
                        rules={[{ required: true, message: "Please enter your name" }]}
                    >
                        <Input prefix={<UserOutlined />} placeholder="Full Name" size="large" />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label="Email Address"
                        rules={[
                            { required: true, message: "Please enter your email" },
                            { type: "email", message: "Please enter a valid email" },
                        ]}
                    >
                        <Input prefix={<MailOutlined />} placeholder="Email Address" size="large" />
                    </Form.Item>

                    <Divider />

                    <Title level={4} style={{ marginBottom: 8 }}>
                        Change Password
                    </Title>
                    <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
                        Leave new password fields empty if you don't want to change it.
                    </Text>

                    <Form.Item
                        name="newPassword"
                        label="New Password"
                        rules={[
                            { min: 6, message: "Password must be at least 6 characters" },
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="New Password"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        label="Confirm New Password"
                        dependencies={["newPassword"]}
                        rules={[
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue("newPassword") === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(
                                        new Error("The two passwords do not match!")
                                    );
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Confirm New Password"
                            size="large"
                        />
                    </Form.Item>

                    <Divider />

                    <Alert
                        message="Required for any changes"
                        description="Please enter your current password to authorize these changes."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />

                    <Form.Item
                        name="currentPassword"
                        label="Current Password"
                        rules={[
                            { required: true, message: "Current password is required to save changes" },
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder="Confirm Current Password"
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            icon={<SaveOutlined />}
                            loading={loading}
                            size="large"
                            block
                        >
                            Update Profile
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    );
};

export default Profile;
